/* iter55p11 — User profile panel.
 *
 * Lets every writer set:
 *   · A pen name (auto-slugged for the public /authors/<slug> URL).
 *   · A portrait image URL (paste any hosted URL; upload path is a
 *     follow-up — the profile still works without one).
 *   · A short bio (280-char Twitter-style pitch).
 *   · Opt-in to offer a sample chapter (pick project + chapter + label).
 *   · Opt-in to appear on the public Landing Author Ticket Rail.
 *   · Opt-in to accept contact messages from fans (email required).
 *
 * Loads/saves against /api/profile/me. Renders as a controlled form
 * with a right-side live "how you look on the landing rail" preview.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save, User, Ticket, MessageCircle, Inbox, Upload, PartyPopper, Bell } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { usePW } from '@/lib/store';
import AuthorInboxPanel from './AuthorInboxPanel';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function UserProfilePanel({ open, onOpenChange }) {
  const { project, chapters } = usePW();
  const [row, setRow]     = useState(null);
  const [busy, setBusy]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);

  useEffect(() => {
    if (!open) return;
    setBusy(true);
    Promise.all([
      axios.get(`${API}/profile/me`, { withCredentials: true }).then((r) => {
        setRow(r.data || {});
        try {
          window.localStorage.setItem('pw_celebration_style', r.data?.celebration_style || 'confetti');
        } catch { /* ignore */ }
      }),
      axios.get(`${API}/profile/me/contact-inbox?limit=1`, { withCredentials: true })
        .then((r) => setInboxUnread(r.data?.unread || 0))
        .catch(() => setInboxUnread(0)),
    ])
      .catch(() => toast.error('Could not load your profile.'))
      .finally(() => setBusy(false));
  }, [open]);

  const set = (k, v) => setRow((prev) => ({ ...prev, [k]: v }));

  const setSocialLink = (idx, k, v) => setRow((prev) => {
    const next = [...(prev.social_links || [])];
    next[idx] = { ...next[idx], [k]: v };
    return { ...prev, social_links: next };
  });
  const addSocialLink = () => setRow((prev) => ({
    ...prev, social_links: [...(prev.social_links || []), { label: '', url: '' }],
  }));
  const removeSocialLink = (idx) => setRow((prev) => ({
    ...prev, social_links: (prev.social_links || []).filter((_, i) => i !== idx),
  }));

  const save = async () => {
    if (!row) return;
    setSaving(true);
    try {
      const payload = {
        pen_name:            row.pen_name || '',
        portrait_url:        row.portrait_url || '',
        bio_short:           row.bio_short || '',
        bio_long:            row.bio_long || '',
        social_links:        (row.social_links || []).filter((l) => l.label?.trim() && l.url?.trim()),
        sample_opt_in:       !!row.sample_opt_in,
        sample_project_id:   row.sample_project_id || null,
        sample_chapter_id:   row.sample_chapter_id || null,
        sample_label:        row.sample_label || '',
        contact_open:        !!row.contact_open,
        contact_email:       row.contact_email || '',
        show_in_ticket_rail: !!row.show_in_ticket_rail,
        stats_opt_in:        !!row.stats_opt_in,
        celebration_style:   row.celebration_style || 'confetti',
        completeness_digest_email: row.completeness_digest_email !== false,  // default true
      };
      const r = await axios.put(`${API}/profile/me`, payload, { withCredentials: true });
      setRow(r.data);
      try {
        window.localStorage.setItem('pw_celebration_style', r.data?.celebration_style || 'confetti');
      } catch { /* ignore */ }
      toast.success('Profile saved.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => (d?.msg || '').replace(/^Value error,\s*/i, '')).filter(Boolean).join('; ') || 'Save failed.'
        : (detail || 'Save failed.');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="user-profile-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-4 w-4" /> Your author profile
            {row?.contact_open && (
              <button
                type="button"
                onClick={() => setInboxOpen(true)}
                data-testid="prof-open-inbox"
                className="ml-auto inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[11px] hover:bg-secondary"
              >
                <Inbox className="h-3 w-3" /> Inbox
                {inboxUnread > 0 && (
                  <span className="rounded-full bg-primary px-1.5 text-[9.5px] font-semibold text-primary-foreground">
                    {inboxUnread}
                  </span>
                )}
              </button>
            )}
          </DialogTitle>
          <DialogDescription>
            Pick a pen name, add a portrait, and choose whether to share a
            sample chapter or appear on the public Landing rail.
          </DialogDescription>
        </DialogHeader>

        {busy || !row ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-[1fr_260px]">
            {/* Form column */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="prof-pen-name">Pen name</Label>
                <Input id="prof-pen-name" data-testid="prof-pen-name"
                  value={row.pen_name || ''}
                  onChange={(e) => set('pen_name', e.target.value)}
                  placeholder="Sable Aventine" />
                {row.pen_slug && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Public URL: <code>/authors/{row.pen_slug}</code>
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="prof-portrait">Portrait</Label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border bg-muted"
                       data-testid="prof-portrait-preview">
                    {row.portrait_url ? (
                      <img src={row.portrait_url.startsWith('/api/')
                            ? ((process.env.REACT_APP_BACKEND_URL || '') + row.portrait_url)
                            : row.portrait_url}
                            alt="" className="h-full w-full object-cover" />
                    ) : (
                      <User className="h-full w-full p-3 text-muted-foreground" />
                    )}
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm border px-3 py-1.5 text-[12px] hover:bg-secondary"
                          data-testid="prof-portrait-upload-label">
                    <Upload className="h-3.5 w-3.5" />
                    Upload · JPG / PNG / WEBP · &lt;5MB
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      data-testid="prof-portrait-upload"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 5 * 1024 * 1024) {
                          toast.error('Portrait must be under 5MB.');
                          return;
                        }
                        const fd = new FormData();
                        fd.append('file', f);
                        try {
                          const r = await axios.post(`${API}/profile/me/portrait`, fd, {
                            withCredentials: true,
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          set('portrait_url', r.data.url);
                          toast.success('Portrait uploaded.');
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Upload failed.');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>
                <p className="mt-1 text-[10.5px] text-muted-foreground">
                  Stored privately on Emergent Object Storage. Public URL is
                  <code> {row.portrait_url || '/api/profile/portrait/<id>'} </code>.
                </p>
              </div>

              <div>
                <Label htmlFor="prof-bio">Short bio · 280 chars</Label>
                <textarea id="prof-bio" data-testid="prof-bio" rows={3}
                  maxLength={280}
                  value={row.bio_short || ''}
                  onChange={(e) => set('bio_short', e.target.value)}
                  className="mt-1 w-full rounded-sm border border-input bg-background p-2 text-sm"
                  placeholder="Writes dark fantasy from an attic in Vienna. Cats optional." />
                <p className="mt-1 text-right text-[10px] text-muted-foreground">
                  {(row.bio_short || '').length}/280
                </p>
              </div>

              <div>
                <Label htmlFor="prof-bio-long">Long bio · up to 2,000 chars</Label>
                <textarea id="prof-bio-long" data-testid="prof-bio-long" rows={6}
                  maxLength={2000}
                  value={row.bio_long || ''}
                  onChange={(e) => set('bio_long', e.target.value)}
                  className="mt-1 w-full rounded-sm border border-input bg-background p-2 text-sm"
                  placeholder="The full story — background, influences, what you're working on next. Shown on your public author page below the short bio." />
                <p className="mt-1 text-right text-[10px] text-muted-foreground">
                  {(row.bio_long || '').length}/2000
                </p>
              </div>

              <div>
                <Label>Social links</Label>
                <div className="mt-1 space-y-2">
                  {(row.social_links || []).map((link, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input data-testid={`prof-social-label-${idx}`}
                        value={link.label || ''}
                        onChange={(e) => setSocialLink(idx, 'label', e.target.value)}
                        placeholder="Instagram"
                        className="w-32" />
                      <Input data-testid={`prof-social-url-${idx}`}
                        value={link.url || ''}
                        onChange={(e) => setSocialLink(idx, 'url', e.target.value)}
                        placeholder="https://instagram.com/you"
                        className="flex-1" />
                      <Button type="button" variant="ghost" size="sm"
                        data-testid={`prof-social-remove-${idx}`}
                        onClick={() => removeSocialLink(idx)}>
                        Remove
                      </Button>
                    </div>
                  ))}
                  {(row.social_links || []).length < 8 && (
                    <Button type="button" variant="outline" size="sm"
                      data-testid="prof-social-add"
                      onClick={addSocialLink}>
                      + Add a link
                    </Button>
                  )}
                </div>
              </div>

              {/* Sample opt-in block */}
              <fieldset className="rounded-sm border p-3 space-y-2">
                <legend className="px-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <Ticket className="mr-1 inline h-3 w-3" /> Sample chapter · build a fan base
                </legend>
                <label className="flex items-start gap-2 text-[13px]">
                  <input type="checkbox" data-testid="prof-sample-optin"
                    className="mt-0.5 h-3.5 w-3.5 accent-primary"
                    checked={!!row.sample_opt_in}
                    onChange={(e) => set('sample_opt_in', e.target.checked)} />
                  <span>
                    Offer a chapter (finished or WIP) as a free sample to visitors —
                    great for teasing an unreleased project.
                  </span>
                </label>
                {row.sample_opt_in && (
                  <div className="grid gap-2 pl-6 md:grid-cols-2">
                    <div>
                      <Label className="text-[11px]">Project</Label>
                      <select
                        data-testid="prof-sample-project"
                        className="mt-1 w-full rounded-sm border border-input bg-background p-1.5 text-[12.5px]"
                        value={row.sample_project_id || ''}
                        onChange={(e) => set('sample_project_id', e.target.value || null)}
                      >
                        <option value="">Select…</option>
                        {project && (
                          <option value={project.id}>{project.title}</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[11px]">Chapter</Label>
                      <select
                        data-testid="prof-sample-chapter"
                        className="mt-1 w-full rounded-sm border border-input bg-background p-1.5 text-[12.5px]"
                        value={row.sample_chapter_id || ''}
                        onChange={(e) => set('sample_chapter_id', e.target.value || null)}
                      >
                        <option value="">Select…</option>
                        {(chapters || []).map((c) => (
                          <option key={c.id} value={c.id}>{c.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[11px]">Sample label</Label>
                      <Input data-testid="prof-sample-label"
                        value={row.sample_label || ''}
                        onChange={(e) => set('sample_label', e.target.value)}
                        placeholder="Ch 1 · Draft — 2,400 words" />
                    </div>
                  </div>
                )}
              </fieldset>

              {/* Contact opt-in */}
              <fieldset className="rounded-sm border p-3 space-y-2">
                <legend className="px-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <MessageCircle className="mr-1 inline h-3 w-3" /> Fan mail
                </legend>
                <label className="flex items-start gap-2 text-[13px]">
                  <input type="checkbox" data-testid="prof-contact-optin"
                    className="mt-0.5 h-3.5 w-3.5 accent-primary"
                    checked={!!row.contact_open}
                    onChange={(e) => set('contact_open', e.target.checked)} />
                  <span>Let readers send me messages via the public landing.</span>
                </label>
                {row.contact_open && (
                  <div className="pl-6">
                    <Label className="text-[11px]">Contact email (kept private)</Label>
                    <Input data-testid="prof-contact-email" type="email"
                      value={row.contact_email || ''}
                      onChange={(e) => set('contact_email', e.target.value)}
                      placeholder="you@example.com" />
                  </div>
                )}
              </fieldset>

              {/* Ticket-rail opt-in */}
              <label className="flex items-start gap-2 rounded-sm border p-3 text-[13px]">
                <input type="checkbox" data-testid="prof-ticket-optin"
                  className="mt-0.5 h-3.5 w-3.5 accent-primary"
                  checked={!!row.show_in_ticket_rail}
                  onChange={(e) => set('show_in_ticket_rail', e.target.checked)} />
                <span>
                  <strong>Appear on the public Landing rail.</strong> Your pen name,
                  portrait, and bio show up under &ldquo;Meet our authors&rdquo; on
                  the front page. Featured authors are chosen by word count each week.
                </span>
              </label>

              {/* iter55p12 — Stats opt-in */}
              <label className="flex items-start gap-2 rounded-sm border p-3 text-[13px]">
                <input type="checkbox" data-testid="prof-stats-optin"
                  className="mt-0.5 h-3.5 w-3.5 accent-primary"
                  checked={!!row.stats_opt_in}
                  onChange={(e) => set('stats_opt_in', e.target.checked)} />
                <span>
                  <strong>Share my numbers on the ticket rail.</strong> Show
                  <em> books sold </em> and <em> books in the pipeline </em> next
                  to my portrait — visitors love a productivity signal. Counts are
                  computed live from your Bookstore sales and unfinished Loom
                  projects; nothing else leaks.
                </span>
              </label>

              {/* Feb-2026 — Celebrate Chapter Finishes: pick a style */}
              <fieldset className="rounded-sm border p-3 space-y-2">
                <legend className="px-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <PartyPopper className="mr-1 inline h-3 w-3" /> Chapter-finish celebration
                </legend>
                <p className="text-[12px] text-muted-foreground">
                  Choose what happens when you finish drafting a chapter.
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    { v: 'confetti', label: 'Confetti' },
                    { v: 'fireworks', label: 'Fireworks' },
                    { v: 'chime', label: 'Toast only' },
                    { v: 'none', label: 'Off' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`prof-celebration-${opt.v}`}
                      onClick={() => set('celebration_style', opt.v)}
                      className={
                        'rounded-sm border px-2 py-1.5 text-[12px] transition-colors '
                        + ((row.celebration_style || 'confetti') === opt.v
                          ? 'border-primary bg-primary/10 font-semibold text-primary'
                          : 'border-input hover:bg-secondary')
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </fieldset>
            </div>

            {/* ── Email notifications ─────────────────────────── */}
            <fieldset className="space-y-2 pt-1">
              <legend className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Bell className="h-3 w-3" /> Email notifications
              </legend>
              <div className="flex items-center justify-between rounded-md border bg-card/50 p-3">
                <div>
                  <p className="text-[13px] font-medium">Weekly manuscript readiness check</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    Receive a weekly email listing any manuscripts missing a cover, synopsis, keywords or word count.
                  </p>
                </div>
                <Switch
                  data-testid="completeness-digest-toggle"
                  checked={row.completeness_digest_email !== false}
                  onCheckedChange={(v) => set('completeness_digest_email', v)}
                />
              </div>
            </fieldset>

            {/* Live preview column */}
            <div className="hidden md:block">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Landing preview
              </Label>
              <div className="mt-2 rounded-md border bg-card p-3 shadow-sm" data-testid="prof-preview">
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 overflow-hidden rounded-full border bg-muted">
                    {row.portrait_url ? (
                      <img src={row.portrait_url.startsWith('/api/')
                            ? ((process.env.REACT_APP_BACKEND_URL || '') + row.portrait_url)
                            : row.portrait_url}
                            alt="" className="h-full w-full object-cover" />
                    ) : (
                      <User className="h-full w-full p-2 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-semibold">{row.pen_name || 'Your pen name'}</div>
                    <div className="text-[10px] text-muted-foreground">/authors/{row.pen_slug || 'you'}</div>
                  </div>
                </div>
                <p className="mt-2 line-clamp-3 text-[11.5px] text-muted-foreground">
                  {row.bio_short || 'Your short bio appears here.'}
                </p>
                {row.sample_opt_in && row.sample_label && (
                  <p className="mt-2 rounded-sm bg-primary/10 px-2 py-1 text-[10.5px] font-semibold text-primary">
                    <Ticket className="mr-1 inline h-3 w-3" /> Sample: {row.sample_label}
                  </p>
                )}
                {row.contact_open && (
                  <p className="mt-1 text-[10.5px] text-muted-foreground">
                    <MessageCircle className="mr-1 inline h-3 w-3" /> Accepting fan mail
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={save} disabled={saving || busy} data-testid="prof-save">
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
            Save profile
          </Button>
        </DialogFooter>
      </DialogContent>
      <AuthorInboxPanel open={inboxOpen} onOpenChange={setInboxOpen} />
    </Dialog>
  );
}
